// =====================================================================
//  Onglet « Code » d'une stratégie OTP.
//  Pattern (longueur / alphabet / casse / groupage), durée de vie,
//  tentatives, et APERÇU d'un code généré (client-only).
// =====================================================================

import {IDisposableLike} from "../../Tools/Disposable/IDisposableLike";
import {
    IOtpStrategyModel,
    OtpAlphabet,
    OtpCase,
} from "../../Model/Otp/OtpStrategyModel.js";
import {OtpEnumHelper} from "./OtpEnumHelper.js";

export class OtpCodeEditor implements IDisposableLike {
    #jqMainContainer: JQuery | undefined;
    #jqFormContainer: JQuery | undefined;
    #jqPreview: JQuery | undefined;
    #model: IOtpStrategyModel;
    #kForm: kendo.ui.Form | undefined;

    constructor(options: IOtpCodeEditorOptions) {
        this.#jqMainContainer = options.jqMainContainer;
        this.#model = options.model;
    }

    public Init = () => {
        this.#buildHtml();
        this.#buildForm();
        this.#refreshPreview();
    };

    #buildHtml = () => {
        const wrap = $("<div></div>")
            .css({display: "flex", gap: "24px", padding: "12px", flexWrap: "wrap"})
            .appendTo(this.#jqMainContainer);

        this.#jqFormContainer = $("<div></div>")
            .css({flex: "1 1 380px", maxWidth: "480px"})
            .appendTo(wrap);

        // Carte d'aperçu
        const card = $("<div></div>")
            .css({
                flex: "0 0 260px",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                padding: "16px",
                height: "fit-content",
                background: "#fafafa",
            })
            .appendTo(wrap);

        $("<div></div>")
            .text("Aperçu du code")
            .css({fontSize: "12px", color: "#888", marginBottom: "8px"})
            .appendTo(card);

        this.#jqPreview = $("<div></div>")
            .css({
                fontFamily: "monospace",
                fontSize: "26px",
                letterSpacing: "2px",
                fontWeight: "600",
                marginBottom: "12px",
                wordBreak: "break-all",
            })
            .appendTo(card);

        $("<button class='k-button k-button-md k-rounded-md k-button-solid k-button-solid-base'>Régénérer</button>")
            .on("click", (e) => {
                e.preventDefault();
                this.#refreshPreview();
            })
            .appendTo(card);
    };

    #buildForm = () => {
        const m = this.#model;
        const formData: any = {
            Length: m.PatternLength ?? 6,
            AlphabetLibelle: OtpEnumHelper.Text(OtpEnumHelper.Alphabet, m.PatternAlphabet ?? OtpAlphabet.Numeric),
            CaseLibelle: OtpEnumHelper.Text(OtpEnumHelper.Case, m.PatternCase ?? OtpCase.Upper),
            GroupSize: m.PatternGroupSize ?? 0,
            GroupSeparator: m.PatternGroupSeparator ?? "-",
            ExpirationMinutes: m.ExpirationMinutes ?? 10,
            MaxAttempts: m.MaxAttempts ?? 5,
        };

        this.#kForm = this.#jqFormContainer
            .kendoForm({
                formData,
                orientation: "vertical",
                size: "small",
                items: [
                    {
                        field: "Length",
                        label: "Longueur du code",
                        editor: "NumericTextBox",
                        editorOptions: {min: 4, max: 12, format: "n0", decimals: 0},
                        validation: {required: true},
                    },
                    {
                        field: "AlphabetLibelle",
                        label: "Alphabet",
                        editor: "DropDownList",
                        editorOptions: {dataSource: OtpEnumHelper.Texts(OtpEnumHelper.Alphabet)},
                        validation: {required: true},
                    },
                    {
                        field: "CaseLibelle",
                        label: "Casse",
                        editor: "DropDownList",
                        editorOptions: {dataSource: OtpEnumHelper.Texts(OtpEnumHelper.Case)},
                        validation: {required: true},
                    },
                    {
                        field: "GroupSize",
                        label: "Taille des groupes (0 = aucun)",
                        editor: "NumericTextBox",
                        editorOptions: {min: 0, max: 6, format: "n0", decimals: 0},
                    },
                    {field: "GroupSeparator", label: "Séparateur de groupe"},
                    {
                        field: "ExpirationMinutes",
                        label: "Durée de vie (minutes)",
                        editor: "NumericTextBox",
                        editorOptions: {min: 1, max: 60, format: "n0", decimals: 0},
                        validation: {required: true},
                    },
                    {
                        field: "MaxAttempts",
                        label: "Tentatives maximum",
                        editor: "NumericTextBox",
                        editorOptions: {min: 1, max: 10, format: "n0", decimals: 0},
                        validation: {required: true},
                    },
                ],
                change: (e: kendo.ui.FormChangeEvent) => {
                    const m = this.#model;
                    switch (e.field) {
                        case "Length":
                            m.PatternLength = e.value;
                            break;
                        case "AlphabetLibelle":
                            m.PatternAlphabet = OtpEnumHelper.Value(OtpEnumHelper.Alphabet, e.value);
                            break;
                        case "CaseLibelle":
                            m.PatternCase = OtpEnumHelper.Value(OtpEnumHelper.Case, e.value);
                            break;
                        case "GroupSize":
                            m.PatternGroupSize = e.value;
                            break;
                        case "GroupSeparator":
                            m.PatternGroupSeparator = e.value;
                            break;
                        case "ExpirationMinutes":
                            m.ExpirationMinutes = e.value;
                            break;
                        case "MaxAttempts":
                            m.MaxAttempts = e.value;
                            break;
                    }
                    this.#refreshPreview();
                },
            })
            .data("kendoForm");

        this.#kForm.element.find(".k-form-buttons").remove();
    };

    /** Génère un exemple de code conforme au pattern courant (maquette, non cryptographique). */
    #refreshPreview = () => {
        const m = this.#model;
        const length = m.PatternLength ?? 6;
        let chars: string;
        switch (m.PatternAlphabet) {
            case OtpAlphabet.Alphanumeric:
                chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
                break;
            case OtpAlphabet.AlphanumericNoAmbiguous:
                chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
                break;
            default:
                chars = "0123456789";
        }

        let raw = "";
        for (let i = 0; i < length; i++)
            raw += chars[Math.floor(Math.random() * chars.length)];

        if (m.PatternCase === OtpCase.Lower) raw = raw.toLowerCase();
        else if (m.PatternCase === OtpCase.Mixed) raw = this.#mixCase(raw);
        // Upper : l'alphabet est déjà en majuscules.

        let display = raw;
        if (m.PatternGroupSize && m.PatternGroupSize > 0) {
            const sep = m.PatternGroupSeparator ?? "-";
            display = (raw.match(new RegExp(`.{1,${m.PatternGroupSize}}`, "g")) ?? []).join(sep);
        }

        this.#jqPreview.text(display);
    };

    #mixCase = (s: string): string =>
        s
            .split("")
            .map((c) => (Math.random() < 0.5 ? c.toLowerCase() : c.toUpperCase()))
            .join("");

    public Dispose = () => {
        if (this.#kForm) this.#kForm.destroy();
        if (this.#jqMainContainer) this.#jqMainContainer.empty();
    };
}

export interface IOtpCodeEditorOptions {
    jqMainContainer: JQuery;
    model: IOtpStrategyModel;
}
